import React from 'react';
import { Icon } from '../icons.jsx';
import { StatusChip, AvatarStack, fmtMoney, fmtPct, fmtDateFull } from '../components.jsx';
import { notify } from '../modals.jsx';
import { BidDetailScreen, TabNav } from './bid-detail.jsx';
import { MOCK_MEMORY_INDEX } from '../mock/memoryIndex.js';

const { useState } = React;

function daysLeft(date) {
  if (!date) return null;
  const d = new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86_400_000);
}

// =========================================================
// Ficha maestra de la licitación (demo)
// Cabecera común + sub-navegación: Pre-análisis · Análisis · Memoria · Actividad
// =========================================================
export function BidMasterDetail({ bidId, back, openEditor }) {
  const bid = window.MockData.BIDS.find((b) => b.id === bidId) || window.MockData.BIDS[0];
  const [tab, setTab] = useState('preanalysis');

  if (!bid) {
    return (
      <div className="viewport-inner fade-in" style={{ padding: 60, textAlign: 'center', color: 'var(--text-3)' }}>
        <Icon.Briefcase size={28} />
        <div style={{ marginTop: 12, fontSize: 15, color: 'var(--text-2)' }}>Esta licitación no existe.</div>
        <button className="btn btn-outline" style={{ marginTop: 18 }} onClick={back}><Icon.ChevronLeft size={14} /> Volver</button>
      </div>
    );
  }

  const dl = daysLeft(bid.deadline);
  const team = (bid.team || []).map((id) => window.MockData.USERS.find((u) => u.id === id)).filter(Boolean);

  const tabs = [
    { id: 'preanalysis', label: 'Pre-análisis', icon: 'Target' },
    { id: 'analysis', label: 'Análisis', icon: 'Sparkles' },
    { id: 'memoria', label: 'Memoria técnica', icon: 'PenTool' },
    { id: 'activity', label: 'Actividad', icon: 'Activity' },
  ];

  return (
    <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Cabecera común persistente */}
      <div style={{ padding: '22px 36px 0', background: 'var(--bg-0)', borderBottom: '1px solid var(--line-1)' }}>
        <div style={{ maxWidth: 1320, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-3)', marginBottom: 12 }}>
            <button className="btn btn-ghost" onClick={back} style={{ padding: '4px 8px', fontSize: 12 }}><Icon.ChevronLeft size={13} /> Licitaciones</button>
            <span>·</span>
            <span className="font-mono" style={{ color: 'var(--text-4)' }}>{bid.code}</span>
            {bid.lot && <><span>·</span><span>{bid.lot}</span></>}
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 24 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11.5, color: 'var(--text-3)', letterSpacing: '0.03em' }}>{bid.entity}</span>
                <StatusChip status={bid.status} />
                {bid.region && <span className="chip" style={{ fontSize: 10.5 }}>{bid.region}</span>}
              </div>
              <h1 className="page-title" style={{ fontSize: 30 }}>{bid.title}</h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginTop: 10, fontSize: 13, color: 'var(--text-3)', flexWrap: 'wrap' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon.Coins size={13} /> {fmtMoney(bid.budgetBase)}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon.Calendar size={13} /> Cierre {fmtDateFull(bid.deadline)} {dl != null && <span style={{ color: dl < 15 ? 'var(--sem-warning)' : 'var(--text-3)' }}>· en {dl} días</span>}</span>
                {bid.margin != null && <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon.Activity size={13} /> Margen {fmtPct(bid.margin)}</span>}
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon.Users size={13} /> <AvatarStack users={team} size={20} max={4} /></span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button className="btn btn-outline" onClick={() => notify('Exportando ficha completa (demo)', 'Download')}><Icon.Download size={14} /> Exportar</button>
              <button className="btn btn-outline" onClick={() => notify('Lizzi re-analizando el pliego (demo)', 'Sparkles')}><Icon.Sparkles size={14} /> Re-analizar</button>
              <button className="btn btn-outline" onClick={openEditor} title="Base de conocimiento de Lizzi sobre esta licitación"><Icon.Sparkles size={14} /> Memoria activa</button>
              <button className="btn btn-primary" onClick={() => setTab('memoria')}><Icon.PenTool size={14} /> Memoria técnica</button>
            </div>
          </div>

          {/* Sub-navegación de la ficha */}
          <div style={{ marginTop: 14 }}>
            <TabNav tabDefs={tabs} tab={tab} setTab={setTab} />
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {tab === 'preanalysis' && <PreAnalysisTab bid={bid} onLaunch={() => setTab('analysis')} />}
        {tab === 'analysis' && <BidDetailScreen bidId={bid.id} back={back} openEditor={openEditor} embedded />}
        {tab === 'memoria' && <MemoriaTab bid={bid} openEditor={openEditor} />}
        {tab === 'activity' && <ActivityTab bid={bid} />}
      </div>
    </div>
  );
}

// ---------- Tab: Pre-análisis (scorecard bid/no-bid) ----------
function PreAnalysisTab({ bid, onLaunch }) {
  const dl = daysLeft(bid.deadline);
  const criteria = [
    { id: 'fit', label: 'Encaje estratégico', hint: 'Alineación con líneas de negocio de Impact Hub', score: /acelera|emprend|innova|impacto|consultor|formaci/i.test(bid.title) ? 4 : 2 },
    { id: 'budget', label: 'Importe y margen', hint: `Base ${fmtMoney(bid.budgetBase)} · margen ${fmtPct(bid.margin || 0)}`, score: (bid.margin || 0) > 10 ? 4 : (bid.margin || 0) > 5 ? 3 : 2 },
    { id: 'time', label: 'Plazo disponible', hint: dl != null ? `${dl} días hasta el cierre` : 'Sin plazo', score: dl == null ? 2 : dl >= 20 ? 4 : dl >= 10 ? 3 : 1 },
    { id: 'competition', label: 'Posición competitiva', hint: 'Referencias y experiencia previa con el organismo', score: bid.flagship ? 4 : 3 },
  ];
  const total = criteria.reduce((s, c) => s + c.score, 0);
  const pct = Math.round((total / (criteria.length * 4)) * 100);
  const rec = pct >= 70 ? { label: 'Go — adelante con el análisis', sem: 'sem-success', icon: 'CheckCircle' }
    : pct >= 45 ? { label: 'Revisar — decisión del comité', sem: 'sem-warning', icon: 'AlertTriangle' }
    : { label: 'No-Go — bajo encaje', sem: 'sem-danger', icon: 'AlertCircle' };

  const requirements = [
    { label: 'Clasificación empresarial / solvencia técnica', ok: true },
    { label: 'Volumen de negocio mínimo exigido', ok: true },
    { label: 'Experiencia en contratos similares (3 años)', ok: bid.flagship },
    { label: 'Equipo con titulación y dedicación mínima', ok: true },
    { label: 'Certificaciones (ISO 9001 / 27001)', ok: false },
  ];

  return (
    <div className="viewport-inner" style={{ maxWidth: 1320, margin: '0 auto', padding: '28px 36px 80px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 24 }}>
        <div className="card" style={{ padding: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <div className="section-title">Scorecard Bid / No-Bid</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 2 }}>Puntúa el encaje antes de invertir horas en el análisis.</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 30, fontWeight: 700, color: `var(--${rec.sem})` }}>{pct}</div>
              <div style={{ fontSize: 11, color: 'var(--text-4)' }}>/ 100</div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {criteria.map((c) => (
              <div key={c.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                  <span style={{ fontSize: 13.5, color: 'var(--text-1)' }}>{c.label}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{c.score}/4</span>
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--text-4)', marginBottom: 5 }}>{c.hint}</div>
                <div style={{ height: 6, borderRadius: 99, background: 'var(--line-1)', overflow: 'hidden' }}>
                  <div style={{ width: `${(c.score / 4) * 100}%`, height: '100%', background: 'var(--primary)' }} />
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 20, padding: '14px 16px', borderRadius: 10, background: 'var(--bg-2)', display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className={`sem ${rec.sem}`} style={{ width: 34, height: 34, borderRadius: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>{React.createElement(Icon[rec.icon], { size: 17 })}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Recomendación</div>
              <div style={{ fontSize: 14, color: 'var(--text-1)', fontWeight: 500 }}>{rec.label}</div>
            </div>
            <button className="btn btn-primary" onClick={onLaunch}><Icon.Sparkles size={14} /> Lanzar análisis</button>
          </div>
        </div>

        <div className="card" style={{ padding: 22 }}>
          <div className="section-title" style={{ marginBottom: 4 }}>Checklist de requisitos</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginBottom: 16 }}>Solvencia y condiciones de admisión.</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {requirements.map((r) => (
              <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                <span className={`sem ${r.ok ? 'sem-success' : 'sem-warning'}`} style={{ width: 22, height: 22, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                  {r.ok ? <Icon.Check size={12} /> : <Icon.AlertTriangle size={12} />}
                </span>
                <span style={{ color: r.ok ? 'var(--text-1)' : 'var(--text-2)' }}>{r.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Tab: Memoria ----------
function MemoriaTab({ bid, openEditor }) {
  const STATUS_META = {
    done: { label: 'Completada', sem: 'sem-success', pct: 100 },
    review: { label: 'En revisión', sem: 'sem-info', pct: 85 },
    progress: { label: 'En progreso', sem: 'sem-warning', pct: 55 },
    todo: { label: 'Pendiente', sem: 'sem-neutral', pct: 0 },
  };
  const sections = MOCK_MEMORY_INDEX;
  const avg = Math.round(sections.reduce((s, x) => s + (STATUS_META[x.status]?.pct ?? 0), 0) / sections.length);
  const totalPages = sections.reduce((s, x) => s + (x.pages || 0), 0);
  const totalWords = sections.reduce((s, x) => s + (x.words || 0), 0);
  return (
    <div className="viewport-inner" style={{ maxWidth: 1320, margin: '0 auto', padding: '28px 36px 80px' }}>
      <div className="card" style={{ padding: 22, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 20 }}>
        <div style={{ flex: 1 }}>
          <div className="section-title">Memoria técnica</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 2 }}>
            Documento técnico a redactar y presentar en la licitación · {sections.length} secciones · {totalPages} págs · {totalWords.toLocaleString('es-ES')} palabras · {avg}% completado
          </div>
          <div style={{ height: 8, borderRadius: 99, background: 'var(--line-1)', overflow: 'hidden', marginTop: 12, maxWidth: 420 }}>
            <div style={{ width: `${avg}%`, height: '100%', background: 'var(--primary)' }} />
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => notify('Editor de redacción de la memoria técnica — próximamente', 'PenTool')}><Icon.PenTool size={14} /> Abrir editor</button>
      </div>
      <div className="card" style={{ overflow: 'hidden' }}>
        <table className="tbl">
          <thead><tr><th>Sección</th><th style={{ width: 140 }}>Responsable</th><th style={{ width: 90 }}>Págs</th><th style={{ width: 180 }}>Estado</th></tr></thead>
          <tbody>
            {sections.map((s) => {
              const u = window.MockData.USERS.find((x) => x.id === s.owner);
              const meta = STATUS_META[s.status] || STATUS_META.todo;
              return (
                <tr key={s.n}>
                  <td style={{ fontSize: 13.5, paddingLeft: s.child ? 36 : undefined, color: s.child ? 'var(--text-2)' : 'var(--text-1)' }}>
                    <span className="font-mono" style={{ color: 'var(--text-4)', marginRight: 8, fontSize: 12 }}>{s.n}</span>{s.title}
                  </td>
                  <td style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{u?.name || '—'}</td>
                  <td style={{ fontSize: 12.5, color: 'var(--text-3)' }}>{s.pages || '—'}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="dot" style={{ width: 7, height: 7, borderRadius: 99, background: `var(--${meta.sem})` }} />
                      <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{meta.label}</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Tab: Actividad ----------
function ActivityTab({ bid }) {
  const events = [
    { icon: 'Compass', tone: 'sem-info', text: 'Oportunidad detectada en el radar (Infonalia)', when: 'Hace 6 días' },
    { icon: 'CheckCircle', tone: 'sem-success', text: 'Preseleccionada tras scorecard bid/no-bid', when: 'Hace 5 días' },
    { icon: 'Sparkles', tone: 'sem-violet', text: 'Lizzi generó el análisis del pliego', when: 'Hace 3 días' },
    { icon: 'PenTool', tone: 'sem-warning', text: 'Inicio de redacción de la memoria técnica', when: 'Hace 2 días' },
    { icon: 'Activity', tone: 'sem-neutral', text: `Estado actualizado · ${(window.MockData.STATUS_LABELS[bid.status] || {}).label || bid.status}`, when: bid.lastActivity || 'Hace 2 horas' },
  ];
  return (
    <div className="viewport-inner" style={{ maxWidth: 1320, margin: '0 auto', padding: '28px 36px 80px' }}>
      <div className="card" style={{ padding: 22 }}>
        <div className="section-title" style={{ marginBottom: 16 }}>Línea de tiempo</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {events.map((e, i) => {
            const Ico = Icon[e.icon];
            return (
              <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '10px 0' }}>
                <span className={`sem ${e.tone}`} style={{ width: 30, height: 30, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0 }}><Ico size={15} /></span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13.5, color: 'var(--text-1)' }}>{e.text}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-4)', marginTop: 1 }}>{e.when}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
